import React, {FC, useCallback, useEffect, useState} from 'react';
import {View} from 'react-native';
import {
  IChannelFragmentFragment,
  ILiveEventFragmentFragment,
  IVodItemFragmentFragment,
} from '~Api/Graphql/gql/types.generated';
import {IPaywallSetting} from '@src/Hooks/usePaywall';
import {tracker} from '~Services/Amplitude/hooks/tracker';
import PlayerPaywallChoosePlan from './PlayerPaywallChoosePlan/PlayerPaywallChoosePlan';
import PlayerPaywallHome from './PlayerPaywallHome/PlayerPaywallHome';

const viewStyle = {
  flex: 1,
  alignItems: 'center' as const,
  width: '100%',
  height: '100%',
  maxWidth: '100%',
  position: 'relative' as 'relative',
};

type SupportedContentType =
  | IVodItemFragmentFragment
  | ILiveEventFragmentFragment
  | IChannelFragmentFragment;
interface PlayerPaywallProps {
  setting: IPaywallSetting;
  isLive: boolean;
  content: SupportedContentType | undefined;
  secondsWatched?: number;
  displayPaywall: boolean;
}

const getTrackingProps = (
  content: SupportedContentType,
  maxPreviewDurationMinutes: number,
  watchedTimeSeconds3Decimals: number,
) => {
  const {id, title, __typename} = content;

  const trackingProps = {
    id,
    title,
    __typename,
    maxPreviewDurationMinutes,
    watchedTimeSeconds3Decimals,
  };

  return trackingProps;
};

const PlayerPaywall: FC<PlayerPaywallProps> = ({
  setting,
  isLive,
  content,
  secondsWatched = -1,
  displayPaywall,
}) => {
  const [isChoosePlan, setIsChoosePlan] = useState(false);
  const [paywallTracked, setPaywallTracked] = useState(false);
  const {currentPaywall, previewDurationTv, previewDurationVod} = setting;

  const maxPreview = isLive ? previewDurationTv : previewDurationVod;

  useEffect(function trackOnOpened() {
    if (!content) return;
    if (displayPaywall && !paywallTracked) {
      const trackingProps = getTrackingProps(content, maxPreview, secondsWatched);
      tracker.paywall.paywallOpened(trackingProps);
      setPaywallTracked(true);
    }

    if (!displayPaywall && paywallTracked) {
      setPaywallTracked(false);
    }
  }, [displayPaywall, content, maxPreview, secondsWatched, paywallTracked]);

  const handleChoosePlan = useCallback(() => {
    setIsChoosePlan(true);
    if (!content) return;

    const trackingProps = getTrackingProps(content, maxPreview, secondsWatched);
    tracker.paywall.goToSubscription(trackingProps);
  }, [content, isLive, previewDurationTv, previewDurationVod]);

  const paywallContent = isChoosePlan ? (
    <View style={viewStyle}>
      <PlayerPaywallChoosePlan paywallId={currentPaywall?.id} />
    </View>
  ) : (
    <PlayerPaywallHome
      setting={setting}
      isLive={isLive}
      content={content}
      onChoosePlan={handleChoosePlan}
    />
  );

  return displayPaywall ? paywallContent : null;
};

export default PlayerPaywall;
